import { useState, useEffect, useRef } from 'react';
import { Search, ShoppingCart, ChevronLeft, ChevronRight, Filter, Check, Building2, Users, ArrowRight, TrendingUp } from 'lucide-react';
import { shopApi, cartApi, cartStorage, tokenStorage } from '@/user/api';
import type { ProductVO } from '@/types/api-entity';
import { Link,useNavigate,useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Banner from '@/components/Banner';
// 引入新创建的广播组件
import BroadcastTicker, { type BroadcastItem } from '@/components/BroadcastTicker';
// 模拟广播数据 (实际项目中请替换为 API 请求)
const MOCK_BROADCASTS: BroadcastItem[] = [
  { id: '1', company: '北京****公司', contact: '刘**', productName: '6061-T6铝合金棒材', amount: 5, unit: '吨', time: '3分钟前' },
  { id: '2', company: '成都****公司', contact: '张**', productName: 'Q235B热轧钢板 2-20mm', amount: 2, unit: '吨', time: '10分钟前' },
  { id: '3', company: '广州****公司', contact: '陈**', productName: '7075-T651航空铝板', amount: 1, unit: '吨', time: '17分钟前' },
  { id: '4', company: '深圳****公司', contact: '李**', productName: 'HRB400E螺纹钢 φ12-φ32', amount: 5, unit: '吨', time: '20分钟前' },
  { id: '5', company: '杭州****公司', contact: '赵**', productName: '7075-T651航空铝板', amount: 3, unit: '吨', time: '30分钟前' },
];

// 后端商品映射为前端展示结构
function mapProduct(product: ProductVO) {
  const specs = product.specifications
    ? Object.entries(product.specifications).map(([label, value]) => ({ label, value }))
    : [];
  return {
    ...product,
    name: product.productName,
    image: product.mainImage || 'https://via.placeholder.com/300',
    sales: product.salesCount || 0,
    features: product.description || '',
    specifications: specs,
    originalPrice: undefined as number | undefined,
  };
}

export default function Shop() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState<ProductVO[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<string[]>(['全部']);
  const [loading, setLoading] = useState(false);
  const [cartCount, setCartCount] = useState(cartStorage.getCartCount());
  const [addedTip, setAddedTip] = useState(false);

  // 广播数据状态
  const [broadcasts, setBroadcasts] = useState<BroadcastItem[]>([]);

  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const pageSize = 20;
  const totalPages = Math.ceil(total / pageSize);
  const navigate = useNavigate();
    const location = useLocation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // 加载商品分类
  useEffect(() => {
    shopApi.getProductCategories().then((res) => {
      if (res.code === 200 && res.data) {
        setCategories(['全部', ...res.data]);
      }
    });
  }, []);

   // 加载广播数据
  useEffect(() => {
    // TODO: 替换为真实的 API 调用，例如: shopApi.getRecentOrders()
    setBroadcasts(MOCK_BROADCASTS);
  }, []);

  // 加载商品列表
  useEffect(() => {
    setLoading(true);
    shopApi
      .getProductList({
        pageNum: currentPage,
        pageSize,
        keyword: searchKeyword.trim() || undefined,
        category: selectedCategory === '全部' ? undefined : selectedCategory,
      })
      .then((res) => {
        if (res.code === 200 && res.data) {
          setProducts(res.data.list || []);
          setTotal(res.data.total || 0);
        }
      })
      .finally(() => setLoading(false));
  }, [currentPage, searchKeyword, selectedCategory]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = async (e: React.MouseEvent, product: ProductVO) => {
    e.preventDefault();
    e.stopPropagation();
    if (tokenStorage.isLoggedIn()) {
      try {
        await cartApi.addToCart(product.id, 1);
        setCartCount((prev) => prev + 1);
        setAddedTip(true);
        setTimeout(() => setAddedTip(false), 2000);
      } catch {
        // 失败后回退到本地
        cartStorage.addToCart(product, 1);
        setCartCount(cartStorage.getCartCount());
        setAddedTip(true);
        setTimeout(() => setAddedTip(false), 2000);
      }
    } else {
      cartStorage.addToCart(product, 1);
      setCartCount(cartStorage.getCartCount());
      setAddedTip(true);
      setTimeout(() => setAddedTip(false), 2000);
    }
  };
  const getFirstImage = (mainImage: string | string[] | undefined | null): string => {
    if (!mainImage) return 'https://via.placeholder.com/40';
    
    let firstUrl = '';
    if (Array.isArray(mainImage)) {
      firstUrl = mainImage[0] || '';
    } else if (typeof mainImage === 'string') {
      const urls = mainImage.split(',');
      firstUrl = urls.length > 0 ? urls[0].trim() : '';
    }
    
    return firstUrl || 'https://via.placeholder.com/40';
  };
  const handSubmit = (type: 'supplier' | 'buyer') => () => {
    // 1. 判断是否登录
    if (!tokenStorage.isLoggedIn()) {
      // 2. 未登录：跳转到登录页
      // 只传递简单的字符串路径，不要传递整个 location 对象
      navigate('/login', { 
        state: { 
          from: location.pathname, // 只传递路径字符串
          targetPath: '/console/profile',
          targetState: type 
        } 
      });
      return;
    }
    navigate('/console/profile', { 
      state: type
    });
  }
  const handMore = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/investment');
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      {/* 加入购物车成功提示 */}
      {addedTip && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-green-50 text-green-700 rounded-lg shadow-lg text-sm flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
          <Check className="w-4 h-4" />
          已成功加入购物车
        </div>
      )}
      <main className="flex-1 pt-20">
        {/* Banner */}
        <div className="section-container mb-4 pt-6">
          <Banner simple={true} />
          {/* <div className="relative h-48 md:h-64 rounded-2xl overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(https://images.unsplash.com/photo-1563104370-7f9cb9a1274a?w=1920&h=400&fit=crop)`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c5bff]/80 to-transparent" />
            <div className="relative h-full flex items-center section-container">
              <div className="text-white">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">工业材料商城</h1>
                <p className="text-white/80">优质钢材、铝材、不锈钢一站式采购平台</p>
              </div>
            </div>
          </div> */}
        </div>
         {/* 广播播报 */}
        <section className="bg-white border-gray-100 mb-4"> 
          <div className="section-container"> 
             <BroadcastTicker items={broadcasts} />
          </div>
        </section>

        {/* Search & Filter */}
        <section className="bg-white border-b border-gray-100 py-6">
          <div className="section-container">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="relative flex-1 max-w-xl flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="搜索商品名称..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setSearchKeyword(searchQuery);
                        setCurrentPage(1);
                      }
                    }}
                    className="w-full h-11 pl-12 pr-4 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  />
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                <button
                  onClick={() => {
                    setSearchKeyword(searchQuery);
                    setCurrentPage(1);
                  }}
                  className="h-11 px-5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 font-medium"
                >
                  <Search className="w-4 h-4" />
                  搜索
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
              <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-[#1c5bff] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section ref={sectionRef} className="py-12 bg-gray-50">
          <div className="section-container">
            {loading ? (
              <div className="text-center py-16">
                <div className="w-8 h-8 border-2 border-[#1c5bff] border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-gray-500 mt-4">加载中...</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {products.map((product, index) => {
                    const mapped = mapProduct(product);
                    return (
                      <div
                        key={product.id}
                        className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500 ${
                          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                        }`}
                        style={{
                          transitionTimingFunction: 'var(--ease-expo-out)',
                          transitionDelay: `${(index % 5) * 50}ms`,
                        }}
                      >
                        <Link to={`/shop/product/${product.id}`}>
                          {/* Image */}
                          <div className="relative aspect-square overflow-hidden bg-gray-100">
                            <img
                              src={getFirstImage(mapped.image)}
                              alt={mapped.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                          </div>

                          {/* Content */}
                          <div className="p-3">
                            <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 group-hover:text-[#1c5bff] transition-colors">
                              {mapped.name}
                            </h3>

                            <p className="text-xs text-gray-500 line-clamp-1 mb-2 h-4" dangerouslySetInnerHTML={{ __html: mapped.features || '<p></p>' }}></p>

                            <div className="flex items-end gap-2">
                              <span className="text-lg font-bold text-[#1c5bff]">
                                {formatPrice(mapped.price)}
                              </span>
                            </div>

                            <p className="text-xs text-gray-400 mt-1">
                              库存: {product.stock}{product.unit || '吨'} · 已售: {mapped.sales}{product.unit || '吨'}
                            </p>
                          </div>
                        </Link>
                        {/* Add to cart button */}
                        <div className="px-3 pb-3">
                          <button
                            onClick={(e) => handleAddToCart(e, product)}
                            className="w-full py-1.5 text-xs font-medium text-[#1c5bff] border border-[#1c5bff] rounded-lg hover:bg-[#1c5bff] hover:text-white transition-all duration-300 flex items-center justify-center gap-1"
                          >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            加入购物车
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Empty State */}
                {products.length === 0 && (
                  <div className="text-center py-16">
                    <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">未找到相关商品</p>
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('全部');
                      }}
                      className="mt-4 text-[#1c5bff] hover:underline"
                    >
                      清除筛选条件
                    </button>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1c5bff] hover:border-[#1c5bff] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-10 h-10 rounded-lg font-medium transition-all duration-300 ${
                          page === currentPage
                            ? 'bg-[#1c5bff] text-white'
                            : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1c5bff] hover:border-[#1c5bff] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Page Info */}
                {products.length > 0 && (
                  <p className="text-center text-gray-500 text-sm mt-4">
                    共 {total} 件商品，当前第 {currentPage}/{totalPages} 页
                  </p>
                )}
              </>
            )}
          </div>
        </section>
        {/* 招商中心入驻入口 */}
        <section className="bg-gradient-to-r from-blue-50 to-purple-50 py-8">
          <div className="section-container">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 供应商入驻 */}
              <div onClick={handSubmit('supplier')}
                className="group relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-[#1c5bff]/30"
              >
                {/* 背景装饰 */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#1c5bff]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                
                <div className="relative z-10">
                  <div className="flex items-start gap-4">
                    {/* 图标 */}
                    <div className="w-14 h-14 rounded-xl bg-[#1c5bff]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1c5bff] transition-colors duration-300">
                      <Building2 className="w-7 h-7 text-[#1c5bff] group-hover:text-white transition-colors duration-300" />
                    </div>
                    
                    {/* 内容 */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#1c5bff] transition-colors">
                        供应商入驻
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                        成为我们的优质供应商，拓展销售渠道，提升品牌影响力，共享平台资源
                      </p>
                      
                      {/* 优势标签 */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full">
                          <TrendingUp className="w-3 h-3" />
                          销量增长
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 text-xs rounded-full">
                          <Users className="w-3 h-3" />
                          海量客户
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-600 text-xs rounded-full">
                          <Check className="w-3 h-3" />
                          品牌曝光
                        </span>
                      </div>
                      
                      {/* 入驻按钮 */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#1c5bff] font-medium text-sm group-hover:gap-3 transition-all duration-300">
                          <span>立即入驻</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                        <div onClick={(e) => handMore(e)}
                          className="flex items-center gap-1 text-gray-500 font-medium text-sm hover:text-[#1c5bff] transition-colors"
                        >
                          <span>了解更多</span>
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 采购商入驻 */}
              <div onClick={handSubmit('buyer')}
                className="group relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-[#1c5bff]/30"
              >
                {/* 背景装饰 */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                
                <div className="relative z-10">
                  <div className="flex items-start gap-4">
                    {/* 图标 */}
                    <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500 transition-colors duration-300">
                      <Users className="w-7 h-7 text-purple-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    
                    {/* 内容 */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                        采购商入驻
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                        加入采购商体系，享受专属优惠，获取优质资源，提升采购效率
                      </p>
                      
                      {/* 优势标签 */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-600 text-xs rounded-full">
                          <TrendingUp className="w-3 h-3" />
                          专属优惠
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full">
                          <Building2 className="w-3 h-3" />
                          优质资源
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 text-xs rounded-full">
                          <Check className="w-3 h-3" />
                          高效采购
                        </span>
                      </div>
                      
                      {/* 入驻按钮 */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-purple-600 font-medium text-sm group-hover:gap-3 transition-all duration-300">
                          <span>立即入驻</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                        <div  onClick={(e) => handMore(e)}
                          className="flex items-center gap-1 text-gray-500 font-medium text-sm hover:text-purple-600 transition-colors"
                        >
                          <span>了解更多</span>
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
